/**
 * Заведение и правка отделения (AK-746, wireframe экран 4).
 *
 * Термин в интерфейсе — «Отделение». Слово «бригада» человеку не показываем
 * (AK-739), хотя таблица в БД называется `brigades`: имя объекта БД переживает
 * модель, но не обязано переживать язык экрана.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { FarmBrigadeRow } from './types';

export interface BrigadeDraft {
  name: string;
  leader_name: string | null;
  leader_phone: string | null;
}

export function BrigadeDialog({
  open,
  brigade,
  pending,
  onCancel,
  onSave,
}: {
  open: boolean;
  /** null — заведение нового отделения. */
  brigade: FarmBrigadeRow | null;
  pending: boolean;
  onCancel: () => void;
  onSave: (draft: BrigadeDraft) => void;
}) {
  const [name, setName] = useState('');
  const [leader, setLeader] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(brigade?.name ?? '');
    setLeader(brigade?.leader_name ?? '');
    setPhone(brigade?.leader_phone ?? '');
    setError(null);
  }, [open, brigade]);

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      // Имя обязательно и только оно: руководителя назначают позже, и
      // требовать его сразу значит не дать завести отделение вовсе.
      setError('Напишите название — например «Первое отделение»');
      return;
    }
    setError(null);
    onSave({
      name: trimmed,
      leader_name: leader.trim() || null,
      leader_phone: phone.trim() || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{brigade ? 'Отделение' : 'Новое отделение'}</DialogTitle>
          <DialogDescription>
            Отделение — управляемый кусок хозяйства. Поля в него раскладываются
            отдельно, во вкладке «Поля»: отметил чекбоксами — «В отделение».
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="brigade-name">Название</Label>
            <Input
              id="brigade-name"
              data-testid="brigade-name"
              value={name}
              disabled={pending}
              aria-invalid={error ? true : undefined}
              className={cn(error && 'border-destructive focus-visible:ring-destructive')}
              onChange={(e) => {
                setName(e.target.value);
                if (error) setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
              }}
            />
            {error && (
              <span role="alert" className="text-xs text-destructive">
                {error}
              </span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="brigade-leader">Руководитель</Label>
            <Input
              id="brigade-leader"
              value={leader}
              disabled={pending}
              placeholder="можно назначить позже"
              onChange={(e) => setLeader(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="brigade-phone">Телефон</Label>
            <Input
              id="brigade-phone"
              value={phone}
              disabled={pending}
              placeholder="+7 900 000-00-00"
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={pending}>
            Отмена
          </Button>
          <Button data-testid="brigade-save" onClick={submit} disabled={pending}>
            {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
